import { APP_TIME_ZONE, localDateKey } from "./formatting";

function readAscii(view, offset, length) {
  let text = "";
  for (let i = 0; i < length; i++) {
    const code = view.getUint8(offset + i);
    if (code === 0) break;
    text += String.fromCharCode(code);
  }
  return text.trim();
}

function parseExifDate(value) {
  const match = String(value || "").match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  return {
    raw: value,
    date: `${year}-${month}-${day}`,
    localDateTime: `${year}-${month}-${day}T${hour}:${minute}:${second}`,
  };
}

function tiffValue(view, tiffStart, entryOffset, littleEndian) {
  const type = view.getUint16(entryOffset + 2, littleEndian);
  const count = view.getUint32(entryOffset + 4, littleEndian);
  const valueOffset = entryOffset + 8;
  const typeSize = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1 }[type] || 1;
  const totalBytes = count * typeSize;
  const absolute = totalBytes <= 4 ? valueOffset : tiffStart + view.getUint32(valueOffset, littleEndian);

  if (type === 2) return readAscii(view, absolute, count);
  if (type === 3) return view.getUint16(absolute, littleEndian);
  if (type === 4) return view.getUint32(absolute, littleEndian);
  return null;
}

function readIfd(view, tiffStart, ifdOffset, littleEndian) {
  if (!ifdOffset) return {};
  const absoluteIfd = tiffStart + ifdOffset;
  if (absoluteIfd < 0 || absoluteIfd + 2 > view.byteLength) return {};
  const count = view.getUint16(absoluteIfd, littleEndian);
  const tags = {};
  for (let i = 0; i < count; i++) {
    const entry = absoluteIfd + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;
    const tag = view.getUint16(entry, littleEndian);
    tags[tag] = tiffValue(view, tiffStart, entry, littleEndian);
  }
  return tags;
}

function parseExif(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return {};

  let offset = 2;
  while (offset + 4 < view.byteLength) {
    if (view.getUint8(offset) !== 0xff) break;
    const marker = view.getUint8(offset + 1);
    const size = view.getUint16(offset + 2);
    if (marker === 0xe1 && readAscii(view, offset + 4, 6) === "Exif") {
      const tiffStart = offset + 10;
      const endian = readAscii(view, tiffStart, 2);
      const littleEndian = endian === "II";
      if (!littleEndian && endian !== "MM") return {};
      const firstIfdOffset = view.getUint32(tiffStart + 4, littleEndian);
      const ifd0 = readIfd(view, tiffStart, firstIfdOffset, littleEndian);
      const exif = readIfd(view, tiffStart, Number(ifd0[0x8769] || 0), littleEndian);
      return {
        dateTime: ifd0[0x0132] || null,
        dateTimeOriginal: exif[0x9003] || null,
        createDate: exif[0x9004] || null,
        width: exif[0xa002] || null,
        height: exif[0xa003] || null,
        orientation: ifd0[0x0112] || null,
      };
    }
    offset += 2 + size;
  }
  return {};
}

function bestDate({ exif, file }) {
  const original = parseExifDate(exif.dateTimeOriginal);
  if (original) return { ...original, source: "exif_original", confidence: "high" };
  const created = parseExifDate(exif.createDate);
  if (created) return { ...created, source: "exif_create", confidence: "high" };
  const modified = parseExifDate(exif.dateTime);
  if (modified) return { ...modified, source: "exif_modify", confidence: "medium" };
  if (file?.lastModified) {
    const date = new Date(file.lastModified);
    return {
      raw: date.toISOString(),
      date: localDateKey(date, APP_TIME_ZONE),
      localDateTime: date.toISOString(),
      source: "file_modified",
      confidence: "medium",
    };
  }
  return { raw: null, date: null, localDateTime: null, source: "app_entry", confidence: "low" };
}

export async function readImageMetadata(file, entryDate) {
  let exif = {};
  try {
    if (/jpe?g/i.test(file?.type || "")) {
      exif = parseExif(await file.arrayBuffer());
    }
  } catch {
    exif = {};
  }

  const date = bestDate({ exif, file });
  const warnings = [];
  if (!exif.dateTimeOriginal && !exif.createDate) warnings.push("No original EXIF capture date found");
  if (date.date && entryDate && date.date !== entryDate) warnings.push(`Photo date ${date.date} differs from entry date ${entryDate}`);
  if (!date.date) warnings.push("Photo date could not be verified");

  return {
    originalFilename: file?.name || null,
    originalMimeType: file?.type || null,
    originalSizeBytes: file?.size || null,
    imageCapturedAt: date.localDateTime,
    imageCapturedDate: date.date,
    imageCapturedRaw: date.raw,
    imageModifiedAt: file?.lastModified ? new Date(file.lastModified).toISOString() : null,
    dateSource: date.source,
    dateConfidence: date.confidence,
    metadataWarnings: warnings,
    exif: {
      dateTimeOriginal: exif.dateTimeOriginal || null,
      createDate: exif.createDate || null,
      modifyDate: exif.dateTime || null,
      width: exif.width || null,
      height: exif.height || null,
      orientation: exif.orientation || null,
    },
  };
}
